import { FC, useState } from 'react'

import { clsx } from 'clsx'

import { Icon } from '../Icon/Icon.js'
import type { IconComponentType } from '../Icon/types.js'
import {
  CheckCircleIcon,
  ExclamationIcon,
  InformationCircleIcon,
  XCircleIcon,
  XIcon,
} from '../icons/index.js'
import { HTMLTribeProps } from '../types/index.js'

export type AlertStatus =
  | 'error'
  | 'warning'
  | 'success'
  | 'info'
  | 'neutral'
  | 'primary'

export type AlertAlign = 'leading' | 'center'

export type AlertProps = HTMLTribeProps<'div'> & {
  status?: AlertStatus
  title?: string
  align?: AlertAlign
  withClose?: boolean
  icon?: IconComponentType
}

/**
 * Alerts are used to communicate a state that affects a system, feature or page.
 */
export const Alert: FC<AlertProps> = ({
  as,
  align = 'leading',
  status,
  title,
  children,
  withClose = false,
  className,
  icon,
  ...rest
}) => {
  const [close, setClose] = useState(false)
  if (close) {
    return null
  }

  if (!icon) {
    switch (status) {
      case 'error':
        icon = <XCircleIcon />
        break
      case 'warning':
        icon = <ExclamationIcon />
        break
      case 'success':
        icon = <CheckCircleIcon />
        break
      case 'neutral':
      case 'primary':
        icon = null
        break
      case 'info':
        icon = <InformationCircleIcon />
        break
      default:
        break
    }
  }

  if (icon !== null) {
    icon = (
      <Icon className="h-5 w-5" aria-hidden="true">
        {icon}
      </Icon>
    )
  }

  const Component = as || 'div'

  return (
    <Component
      className={clsx(
        'relative block rounded-md p-4',
        [
          status === 'error' && 'text-content-on-negative bg-surface-negative',
          status === 'warning' &&
            'text-content-on-attention bg-surface-attention',
          status === 'success' &&
            'text-content-on-positive bg-surface-positive',
          status === 'info' && 'text-content-on-highlight bg-surface-highlight',
          status === 'neutral' && 'text-content-subdued bg-surface-subdued',
          status === 'primary' && 'text-content-on-primary bg-action-primary',
        ],
        className,
      )}
      {...rest}
    >
      <div className={clsx(align === 'center' && 'justify-center', 'flex')}>
        {!!icon && <div className="flex-shrink-0">{icon}</div>}
        <div className={clsx('min-w-0', [!!icon && 'ms-3'])}>
          {title && (
            <h3 className="text-sm font-medium break-words">{title}</h3>
          )}
          {children && (
            <div className={clsx('text-sm', !!title && 'mt-2 ')}>
              {children}
            </div>
          )}
          {withClose && (
            <span className="absolute top-0 end-0 flex items-center me-2 mt-2">
              <button
                onClick={() => setClose(true)}
                type="button"
                className="inline-flex rounded-md p-1.5"
              >
                <span className="sr-only">Dismiss</span>
                <XIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </span>
          )}
        </div>
      </div>
    </Component>
  )
}
